import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Hammer, Home, Building2, Ruler } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "What's the difference between wood and steel framing?",
    answer: "Wood framing is traditional, cost-effective, and widely used in residential construction. Steel framing is lighter, more durable, fire-resistant, and often used in commercial buildings. We help you choose based on your project requirements and budget."
  },
  {
    question: "How long does framing take?",
    answer: "A typical single-family home can be framed in 1-2 weeks, while larger commercial projects may take several weeks. Timeline depends on building size, complexity, and weather conditions."
  },
  {
    question: "Do you handle permits and inspections?",
    answer: "Yes, we coordinate all necessary permits and inspections. Our experienced team ensures all framing meets Ontario Building Code requirements and passes inspections on the first attempt."
  },
  {
    question: "Can you work with my architect's plans?",
    answer: "Absolutely. We work directly from architectural and engineering drawings, ensuring precise execution of your design. We also catch potential issues early and suggest practical solutions."
  }
];

const framingServices = [
  {
    title: "Residential Wood Framing",
    description: "Expert stick-built and engineered lumber framing for new homes, additions, and renovations.",
    icon: Home,
    features: ["Floor systems", "Wall framing", "Roof trusses", "Window/door openings"]
  },
  {
    title: "Commercial Steel Framing",
    description: "Precision steel stud framing for commercial buildings, warehouses, and multi-unit developments.",
    icon: Building2,
    features: ["Load-bearing walls", "Interior partitions", "Ceiling systems", "Fire-rated assemblies"]
  },
  {
    title: "Structural Layouts",
    description: "Accurate measurements and layouts ensuring proper alignment and structural integrity.",
    icon: Ruler,
    features: ["Foundation layout", "Level verification", "Plumb walls", "Engineered solutions"]
  },
  {
    title: "Custom Framing",
    description: "Specialized framing for vaulted ceilings, curved walls, and architectural features.",
    icon: Hammer,
    features: ["Cathedral ceilings", "Bay windows", "Coffered ceilings", "Complex roof lines"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "Ottawa", link: "/locations/ottawa" }
];

export default function FramingServices() {
  return (
    <>
      <SEOMeta
        title="Framing Contractor — Residential & Commercial | Niagara • Hamilton • GTA"
        description="Reliable wood and steel framing for homes and buildings across Niagara, Hamilton & the GTA. Accurate layouts and fast, safe execution."
        keywords="framing contractor Niagara, Hamilton framers, GTA framing service, wood framing, steel framing, wall construction, trusses"
        canonicalUrl="https://blueknightcontracting.ca/services/framing"
      />
      <LocalBusinessSchema
        locationName="Framing Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional framing services for residential and commercial construction"
        services={["Wood Framing", "Steel Framing", "Roof Trusses", "Structural Layout", "Commercial Framing"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Professional Framing Services
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Precision wood and steel framing across Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get Free Quote <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797">
                  <Phone className="h-5 w-5" /> (289) 937-0797
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Framing Expertise</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From single-family homes to large commercial buildings, we deliver accurate, code-compliant framing
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {framingServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Blue Knight Framing?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Code-Compliant Work</CardTitle>
                  <CardDescription>
                    Every project meets or exceeds Ontario Building Code standards with proper engineering where required
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Experienced Crews</CardTitle>
                  <CardDescription>
                    Our framers have decades of combined experience in residential and commercial construction
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>On-Time Delivery</CardTitle>
                  <CardDescription>
                    We understand framing is on the critical path. We deliver quality work on schedule to keep projects moving
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Service Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Need Expert Framing Contractors?</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Get a detailed quote for your residential or commercial framing project
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="tel:+12899370797">(289) 937-0797</a>
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
